export interface Api {
  url: string;
  user: string;
  pass: string;
  ushChannel: string;

  movieUrl: (movieId: string | number) => string; // /movies/${movieId}
  moviesUrl: () => string; // /movies
  moviesInCinemasUrl: () => string; // /movies/in-cinemas/
  moviesInCinemasNearestCitiesUrl: () => string; // /movies/in-cinemas/nearest-cities/
  moviesWatchAtHomeUrl: () => string; // /movies/watch-at-home/
  moviesFestivalsUrl: () => string; // /movies/festivals/
  moviesFestivalTypesUrl: () => string; // /movies/festivalTypes/
  moviesSearchUrl: () => string; // /movies/search/
  moviesAtFestivalsUrl: () => string; // /movies/at-festivals/
  moviesTagUrl: () => string; // /movies/tag/
  moviesEnquiriesUrl: () => string; // /movies/enquiries/
  moviesComingSoonUrl: () => string; // /movies/coming-soon/
  moviesWatchAtHomeSoonUrl: () => string; // /movies/watch-at-home-soon/

  streamingPlatformsAvailableUrl: () => string; // /streamingPlatforms/available/
  streamingLinksUrl: () => string; // /stramingLinks/

  tagsUrl: () => string; // /tags/
  tagsTypesUrl: () => string; // /tags/types

  newsPostsUrl: () => string; // /newsPosts/

  attributesUrl: () => string; // /attributes

  countriesUrl: () => string; // /countries/
  countriesAllUrl: () => string; // /countries/all/

  genresUrl: () => string; // /genres

  languagesUrl: () => string; // /languages/

  userActionsFollowTagsUrl: () => string; // /user-actions/follow/tags/
  userActionsFollowStreamingsUrl: () => string; // /user-actions/follow/streamings/
  userActionsFollowMeUrl: () => string; //'/user-actions/follow-me/users/'
  userActionsFollowUrl: () => string; //'/user-actions/follow/users/';
  userActionsFollowListsUrl: () => string; //'/user-actions/follow/users/lists';
  userActionsProfileUrl: () => string; // /user-actions/profile/
  userActionsSearchListUrl: () => string; // /user-actions/search/list/

  movieActionsWishlist: () => string; ///movie-actions/wishlist/'
  movieActionsWatched: () => string; ///movie-actions/watched/'
  movieActionsList: () => string; ///movie-actions/list/'
  movieActionsSearchUser: () => string; // /movie-actions/search/user/
  movieActionsListMovies: () => string; // /movie-actions/list/movies/
  movieActionsRating: () => string; // /movie-actions/rating/
  movieActionsUserStatus: () => string; // /movie-actions/user-status/
  movieActionsFollow: () => string; // /movie-actions/follow/
  movieActionsFollowMe: () => string; // /movie-actions/follow-me/users/

  playerPurchasedUrl: () => string; // /player/purchased/
  playerFreeOrderUrl: () => string; // /player/free-order

  /*  */
  competitions: () => string; // /competitions/
  consumerLead: () => string; // /consumer-lead
  contractualPartners: () => string; // /contractual-partners

  socialFeedUrl: () => string; // /socialFeed

  channels: () => string; // /channels
  channelTags: () => string; // /channels/tags
  channelVideos: () => string; // /channels/channel-videos

  channelContractualPartners: () => string; // /channels/contractual-partners

  locationUrl: () => string; // /location/

  translationsUrl: () => string; // /translations/

  cinemaChannel: (cinemaId: number) => string; // /cinemaChannel/${cinemaId}

  cinemasUrl: () => string; // /cinemas/
  cinemaUrl: (slug: string) => string; // /cinemas/${slug}
  showtimeUrl: (id: number) => string; // /showtimes/${id}
  showtimesUrl: () => string; // /showtimes/
  showtimesGroupUrl: () => string; // /showtimes/group/
}

type VersionOverrides = {
  [version: string]: string[]; // A map where keys are versions (e.g., 'v2', 'v3') and values are arrays of paths
};

export const generateApi = ({
  url,
  ushChannel,
  user,
  pass,
  versionOverrides = {}
}: {
  url: string;
  ushChannel: string;
  user: string;
  pass: string;
  versionOverrides?: VersionOverrides;
}): Api => {
  const withVersion = (basePath: string, optionalPath: string = '') => {
    const fullPath = optionalPath ? `${basePath}${optionalPath}` : basePath;

    const version = Object.keys(versionOverrides).find(v =>
      versionOverrides[v].some(overridePath => overridePath === fullPath)
    );

    return `${url}/${basePath}${version ? `${version}/` : ''}${optionalPath}/`.replace(/\/+$/, '/');
  };

  return {
    url,
    user,
    pass,
    ushChannel,

    movieUrl: movieId => withVersion(`movies/${movieId}`),
    moviesUrl: () => withVersion('movies/'),
    moviesInCinemasUrl: () => withVersion('movies/', 'in-cinemas/'),
    moviesInCinemasNearestCitiesUrl: () => withVersion('movies/in-cinemas/nearest-cities/'),
    moviesWatchAtHomeUrl: () => withVersion('movies/', 'watch-at-home/'),
    moviesFestivalsUrl: () => withVersion('movies/festivals/'),
    moviesFestivalTypesUrl: () => withVersion('movies/festivalTypes/'),
    moviesSearchUrl: () => withVersion('movies/search/'),
    moviesAtFestivalsUrl: () => withVersion('movies/at-festivals/'),
    moviesTagUrl: () => withVersion('movies/tag/'),
    moviesEnquiriesUrl: () => withVersion('movies/enquiries/'),
    moviesComingSoonUrl: () => withVersion('movies/coming-soon/'),
    moviesWatchAtHomeSoonUrl: () => withVersion('movies/v2/watch-at-home-soon/'),

    streamingPlatformsAvailableUrl: () => withVersion('streamingPlatforms/available/'),

    streamingLinksUrl: () => withVersion('streamingLinks/'),

    tagsUrl: () => withVersion('tags/'),
    tagsTypesUrl: () => withVersion('tags/types'),

    newsPostsUrl: () => withVersion('news-posts/'),

    attributesUrl: () => withVersion('attributes/'),

    countriesUrl: () => withVersion('countries/'),
    countriesAllUrl: () => withVersion('countries/all/'),

    genresUrl: () => withVersion('genres/'),

    languagesUrl: () => withVersion('languages/'),

    userActionsFollowTagsUrl: () => withVersion('user-actions/follow/tags/'),
    userActionsFollowStreamingsUrl: () => withVersion('user-actions/follow/streamings/'),
    userActionsFollowMeUrl: () => withVersion('user-actions/follow-me/users/'),
    userActionsFollowUrl: () => withVersion('user-actions/follow/users/'),
    userActionsFollowListsUrl: () => withVersion('user-actions/follow/users/lists/'),
    userActionsProfileUrl: () => withVersion('user-actions/profile/'),
    userActionsSearchListUrl: () => withVersion('user-actions/search/list/'),

    movieActionsWishlist: () => withVersion('movie-actions/wishlist/'),
    movieActionsWatched: () => withVersion('movie-actions/watched/'),
    movieActionsList: () => withVersion('movie-actions/list/'),
    movieActionsSearchUser: () => withVersion('movie-actions/search/user/'),
    movieActionsListMovies: () => withVersion('movie-actions/list/movies/'),
    movieActionsRating: () => withVersion('movie-actions/rating/'),
    movieActionsUserStatus: () => withVersion('movie-actions/user-status/'),
    movieActionsFollow: () => withVersion('movie-actions/follow/'),
    movieActionsFollowMe: () => withVersion('movie-actions/follow-me/users/'),

    playerPurchasedUrl: () => withVersion('player/purchased/'),
    playerFreeOrderUrl: () => withVersion('player/free-order/'),

    competitions: () => withVersion('competitions/'),
    consumerLead: () => withVersion('consumer-lead/'),
    contractualPartners: () => withVersion('contractual-partners/'),

    socialFeedUrl: () => withVersion('socialFeed/'),

    channels: () => withVersion('channels/'),
    channelTags: () => withVersion('channels/tags/'),
    channelVideos: () => withVersion('channels/', 'channel-videos/'),
    channelContractualPartners: () => withVersion('channels/contractual-partners/'),

    locationUrl: () => withVersion('location/'),

    translationsUrl: () => withVersion('translations/'),

    cinemaChannel: cinemaId => withVersion(`cinemaChannel/${cinemaId}/`),

    cinemasUrl: () => withVersion('cinemas/'),
    cinemaUrl: slug => withVersion(`cinemas/${slug}`),

    showtimeUrl: id => withVersion(`showtimes/${id}`),
    showtimesUrl: () => withVersion(`showtimes/`),
    showtimesGroupUrl: () => withVersion(`showtimes/group/`)
  };
};

export const generateEmptyApi = (): Api => ({
  url: '',
  user: '',
  pass: '',
  ushChannel: '',

  movieUrl: () => '', // Placeholder
  moviesUrl: () => '',
  moviesInCinemasUrl: () => '',
  moviesInCinemasNearestCitiesUrl: () => '',
  moviesWatchAtHomeUrl: () => '',
  moviesWatchAtHomeSoonUrl: () => '',
  moviesFestivalsUrl: () => '',
  moviesFestivalTypesUrl: () => '',
  moviesSearchUrl: () => '',
  moviesAtFestivalsUrl: () => '',
  moviesTagUrl: () => '',
  moviesEnquiriesUrl: () => '',
  moviesComingSoonUrl: () => '',

  streamingPlatformsAvailableUrl: () => '',
  streamingLinksUrl: () => '',

  tagsUrl: () => '',
  tagsTypesUrl: () => '',

  newsPostsUrl: () => '',

  attributesUrl: () => '',

  countriesUrl: () => '',
  countriesAllUrl: () => '',

  genresUrl: () => '',

  languagesUrl: () => '',

  userActionsFollowTagsUrl: () => '',
  userActionsFollowStreamingsUrl: () => '',
  userActionsFollowMeUrl: () => '',
  userActionsFollowUrl: () => '',
  userActionsFollowListsUrl: () => '',
  userActionsProfileUrl: () => '',
  userActionsSearchListUrl: () => '',

  movieActionsWishlist: () => '',
  movieActionsWatched: () => '',
  movieActionsList: () => '',
  movieActionsSearchUser: () => '',
  movieActionsListMovies: () => '',
  movieActionsRating: () => '',
  movieActionsUserStatus: () => '',
  movieActionsFollow: () => '',
  movieActionsFollowMe: () => '',

  playerPurchasedUrl: () => '',
  playerFreeOrderUrl: () => '',

  competitions: () => '',
  consumerLead: () => '',
  contractualPartners: () => '',

  socialFeedUrl: () => '',

  channels: () => '',
  channelTags: () => '',
  channelVideos: () => '',
  channelContractualPartners: () => '',

  locationUrl: () => '',

  translationsUrl: () => '',

  cinemaChannel: () => '',
  cinemasUrl: () => '',
  cinemaUrl: () => '',

  showtimeUrl: () => '',
  showtimesUrl: () => '',
  showtimesGroupUrl: () => ''
});
